/**
 * ConversationalSurveyFlow — experimental voice-driven, discussion-guide survey.
 *
 * Rendered by SurveySession when survey.ai_features.survey_style === 'conversational'. The
 * traditional one-question-per-screen flow is left completely untouched.
 *
 * Loop: ask the AI moderator for the next question → speak it (browser TTS) → respondent answers by
 * voice (reusing the existing /api/transcribe server transcription) → submit the turn → repeat,
 * until the moderator says it's complete. The voice/transcription logic is intentionally COPIED
 * from SurveySession.tsx rather than refactored, to avoid touching the large working file.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mic, Square, RotateCcw, Volume2, VolumeX, Loader2, Send } from 'lucide-react';
import {
  Survey,
  getNextModeratorQuestion,
  submitModeratorAnswer,
  completeSurveySession,
  ModeratorNextResult,
} from '../../utils/surveyApi';
import { useActiveQuestionTime } from './useActiveQuestionTime';
import { VOICE_CAPTURE } from '../../config/constants';
import { UI_TEXT } from '../../config/ui-text';
import { isBlobLongEnough, isReadyChunk } from '../../utils/audioCapture';

// How a conversational answer was produced, reported as the turn's input_mode.
const INPUT_MODE = { MIC: 'mic', TEXT: 'text' } as const;
type InputMode = (typeof INPUT_MODE)[keyof typeof INPUT_MODE];

interface ConversationalSurveyFlowProps {
  survey: Survey;
  sessionId: number;
}

const ConversationalSurveyFlow: React.FC<ConversationalSurveyFlowProps> = ({ survey, sessionId }) => {
  const navigate = useNavigate();

  const [current, setCurrent] = useState<ModeratorNextResult | null>(null);
  const [response, setResponse] = useState('');
  const [questionStartTime, setQuestionStartTime] = useState<string>('');
  const [isThinking, setIsThinking] = useState(true); // moderator is deciding the next question
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turnCount, setTurnCount] = useState(0);
  const [coveredTopics, setCoveredTopics] = useState<string[]>([]);
  // Track how the current answer was produced so we report the real input mode (not always 'mic').
  // Whichever happened most recently before submit wins: transcription -> 'mic', typing -> 'text'.
  // The textarea is locked while transcribing, so a transcription can't clobber a deliberate edit.
  const [lastInputMode, setLastInputMode] = useState<InputMode>(INPUT_MODE.TEXT);

  // True active-time per moderator turn. This flow only renders for real persisted sessions
  // (SurveySession's preview mode never reaches it), so tracking is always enabled here.
  const { getTimings: getActiveTimings, resetForQuestion: resetActiveTime } = useActiveQuestionTime(true);

  const ttsSupported = typeof window !== 'undefined' && 'speechSynthesis' in window;
  const [voiceOn, setVoiceOn] = useState(ttsSupported);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const [micStatus, setMicStatus] = useState<'idle' | 'warming' | 'listening'>('idle');
  const readinessTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const readinessFailedRef = useRef(false);

  // ---------------------------------------------------------------- text-to-speech (browser)
  const speak = useCallback((text?: string | null) => {
    if (!voiceOn || !ttsSupported || !text) return;
    try {
      window.speechSynthesis.cancel();
      const utter = new SpeechSynthesisUtterance(text);
      utter.rate = 1.0;
      utter.pitch = 1.0;
      window.speechSynthesis.speak(utter);
    } catch {
      /* TTS is best-effort; ignore failures */
    }
  }, [voiceOn, ttsSupported]);

  const stopSpeaking = useCallback(() => {
    if (ttsSupported) {
      try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
    }
  }, [ttsSupported]);

  // ----------------------------------------------------------------- moderator turn loop
  const fetchNextQuestion = useCallback(async () => {
    setIsThinking(true);
    setError(null);
    try {
      const result = await getNextModeratorQuestion(sessionId);
      if (result.action === 'complete') {
        // Optional spoken closing line before we finish.
        if (result.question_text) speak(result.question_text);
        await completeSurveySession(sessionId);
        navigate(`/survey-complete/${sessionId}`);
        return;
      }
      setCurrent(result);
      setCoveredTopics(result.covered_topics || []);
      setResponse('');
      setLastInputMode(INPUT_MODE.TEXT);
      setQuestionStartTime(new Date().toISOString());
      resetActiveTime();
      speak(result.question_text);
    } catch (e) {
      console.error('moderator/next failed:', e);
      setError('Something went wrong reaching the moderator. Please try again.');
    } finally {
      setIsThinking(false);
    }
  }, [sessionId, navigate, speak, resetActiveTime]);

  // Kick off the first question on mount.
  useEffect(() => {
    fetchNextQuestion();
    return () => {
      stopSpeaking();
      // Don't let a pending readiness timeout fire after unmount (navigating away
      // mid-warmup) and setState on a gone component.
      if (readinessTimerRef.current) clearTimeout(readinessTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!current || !current.turn_id || !response.trim() || isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    stopSpeaking();
    try {
      const activeTimings = getActiveTimings();
      await submitModeratorAnswer(sessionId, {
        turn_id: current.turn_id,
        moderator_question: current.question_text || '',
        response_text: response.trim(),
        is_mandatory: current.is_mandatory,
        mandatory_question_id: current.mandatory_question_id,
        covered_topics: current.covered_topics,
        input_mode: lastInputMode,
        question_start_time: questionStartTime || undefined,
        // Fail-open: when timing capture is unavailable the fields are simply omitted.
        ...(activeTimings ?? {}),
      });
      setTurnCount((n) => n + 1);
      await fetchNextQuestion();
    } catch (e) {
      console.error('moderator/answer failed:', e);
      setError('Could not save your answer. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [current, response, isSubmitting, sessionId, questionStartTime, lastInputMode, stopSpeaking, fetchNextQuestion, getActiveTimings]);

  // ----------------------------------------------------------------- voice capture (copied)
  const pollTranscriptionResult = useCallback(async (taskId: string): Promise<string> => {
    const maxAttempts = 30;
    const pollInterval = 1000;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/transcribe/status/${taskId}`);
      if (!res.ok) throw new Error('Failed to check transcription status');
      const result = await res.json();
      if (result.status === 'completed') return result.text || '';
      if (result.status === 'failed') throw new Error(result.error || 'Transcription failed');
      await new Promise((r) => setTimeout(r, pollInterval));
    }
    throw new Error('Transcription timed out');
  }, []);

  const transcribeAudio = useCallback(async (audioBlob: Blob, mimeType: string) => {
    try {
      setIsTranscribing(true);
      const ext = mimeType.includes('webm') ? '.webm'
        : mimeType.includes('mp4') ? '.mp4'
        : mimeType.includes('wav') ? '.wav' : '.webm';
      const formData = new FormData();
      formData.append('audio', audioBlob, `recording${ext}`);
      formData.append('context_type', 'survey');
      const turnId = current?.turn_id;
      formData.append('context_metadata', JSON.stringify({
        session_id: String(sessionId),
        survey_id: String(survey.id),
        ...(turnId ? { turn_id: String(turnId) } : {}),
      }));
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/transcribe`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) throw new Error(`Transcription failed: ${res.status}`);
      const result = await res.json();
      if (result.status === 'processing' && result.task_id) {
        const text = await pollTranscriptionResult(result.task_id);
        setResponse((prev) => (prev ? `${prev} ${text}` : text));
        setLastInputMode(INPUT_MODE.MIC);
      } else {
        throw new Error(result.error || 'Transcription failed');
      }
    } catch (e) {
      console.error('Transcription error:', e);
      setError(UI_TEXT.AI_SURVEY.CONVERSATIONAL.ERRORS.TRANSCRIBE_FAILED);
    } finally {
      setIsTranscribing(false);
    }
  }, [pollTranscriptionResult, sessionId, survey, current]);

  const startRecording = useCallback(async () => {
    readinessFailedRef.current = false;
    try {
      stopSpeaking(); // don't record the moderator's own voice
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 44100 },
      });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm'
        : MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4'
        : MediaRecorder.isTypeSupported('audio/wav') ? 'audio/wav' : '';
      if (!mimeType) throw new Error('No supported audio format found');

      const mediaRecorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 128000 });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (isReadyChunk(event.data.size)) {
          audioChunksRef.current.push(event.data);
          // First real chunk = capture is actually live; only now is it safe to
          // show "recording" (on a slow device the mic isn't capturing yet at start).
          if (readinessTimerRef.current) {
            clearTimeout(readinessTimerRef.current);
            readinessTimerRef.current = null;
            setMicStatus('listening');
          }
        }
      };
      mediaRecorder.onstop = async () => {
        // Readiness timeout already handled failure + cleared chunks; don't run the
        // too-short path over the empty buffer and clobber MIC_NOT_READY.
        if (readinessFailedRef.current) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        if (!isBlobLongEnough(audioBlob.size, VOICE_CAPTURE.MIN_BLOB_BYTES)) {
          setError(UI_TEXT.AI_SURVEY.SESSION.ERRORS.RECORDING_TOO_SHORT);
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        await transcribeAudio(audioBlob, mimeType);
        stream.getTracks().forEach((t) => t.stop());
      };
      mediaRecorder.onerror = () => {
        if (readinessTimerRef.current) {
          clearTimeout(readinessTimerRef.current);
          readinessTimerRef.current = null;
        }
        setError(UI_TEXT.AI_SURVEY.SESSION.ERRORS.RECORDING_FAILED);
        setMicStatus('idle');
        stream.getTracks().forEach((t) => t.stop());
      };
      setMicStatus('warming');
      mediaRecorder.start(VOICE_CAPTURE.TIMESLICE_MS);
      // No audio within the window => capture never came up; fail to typing.
      readinessTimerRef.current = setTimeout(() => {
        readinessTimerRef.current = null;
        readinessFailedRef.current = true; // tell onstop to skip the too-short path
        try { if (mediaRecorder.state === 'recording') mediaRecorder.stop(); } catch { /* noop */ }
        stream.getTracks().forEach((t) => t.stop());
        audioChunksRef.current = [];
        setMicStatus('idle');
        setError(UI_TEXT.AI_SURVEY.SESSION.ERRORS.MIC_NOT_READY);
      }, VOICE_CAPTURE.READINESS_TIMEOUT_MS);
    } catch (e) {
      console.error('Error starting recording:', e);
      setError(UI_TEXT.AI_SURVEY.SESSION.ERRORS.MIC_PERMISSION);
      setMicStatus('idle');
    }
  }, [transcribeAudio, stopSpeaking]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    if (readinessTimerRef.current) {
      clearTimeout(readinessTimerRef.current);
      readinessTimerRef.current = null;
    }
    setMicStatus('idle');
  }, []);

  // ----------------------------------------------------------------------------- render
  const busy = isThinking || isSubmitting;

  return (
    <div className="min-h-screen bg-[#FEFEFE] flex flex-col items-center px-4 py-10">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-lg font-semibold text-[#1C1C1E]">{survey.title || survey.name}</h1>
            <p className="text-xs text-[#9CA3AF]">A conversation, not a form — answer out loud, like you're chatting.</p>
          </div>
          {ttsSupported && (
            <button
              onClick={() => { setVoiceOn((v) => { if (v) stopSpeaking(); else speak(current?.question_text); return !v; }); }}
              className="flex items-center gap-1.5 text-xs font-medium text-[#374151] border border-[#E5E7EB] rounded-lg px-3 py-2 hover:bg-[#FAFBFC] transition-colors"
              title={voiceOn ? 'Mute the moderator voice' : 'Let the moderator speak'}
            >
              {voiceOn ? <Volume2 className="h-4 w-4 text-[#07E5D1]" /> : <VolumeX className="h-4 w-4 text-[#9CA3AF]" />}
              {voiceOn ? 'Voice on' : 'Voice off'}
            </button>
          )}
        </div>

        {/* Progress hint */}
        {(turnCount > 0 || coveredTopics.length > 0) && (
          <div className="mb-4 text-xs text-[#6B7280]">
            {turnCount} {turnCount === 1 ? 'question' : 'questions'} so far
            {coveredTopics.length > 0 && <span> · covered: {coveredTopics.join(', ')}</span>}
          </div>
        )}

        {/* Moderator question */}
        <div className="bg-white border border-[#F1F5F9] rounded-xl p-6 shadow-sm min-h-[120px] flex items-center">
          {isThinking ? (
            <div className="flex items-center gap-3 text-[#6B7280]">
              <Loader2 className="h-5 w-5 animate-spin text-[#07E5D1]" />
              <span className="text-sm">The moderator is thinking…</span>
            </div>
          ) : (
            <div className="w-full">
              {current?.is_mandatory && (
                <span className="inline-block mb-2 text-[10px] font-semibold uppercase tracking-wide text-[#07E5D1] bg-[#E8FCFA] rounded px-2 py-0.5">
                  Key question
                </span>
              )}
              <p className="text-xl text-[#1C1C1E] leading-relaxed">{current?.question_text}</p>
              {ttsSupported && current?.question_text && (
                <button
                  onClick={() => speak(current?.question_text)}
                  className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#6B7280] hover:text-[#07E5D1] transition-colors"
                >
                  <RotateCcw className="h-3.5 w-3.5" /> Repeat question
                </button>
              )}
            </div>
          )}
        </div>

        {/* Answer area */}
        {!isThinking && (
          <div className="mt-6">
            <div className="flex flex-col items-center gap-4">
              {/* Mic button */}
              <button
                onClick={micStatus !== 'idle' ? stopRecording : startRecording}
                disabled={isTranscribing || isSubmitting}
                className={`h-20 w-20 rounded-full flex items-center justify-center transition-all disabled:opacity-50 ${
                  micStatus === 'listening'
                    ? 'bg-[#EF4444] animate-pulse shadow-lg'
                    : 'bg-[#07E5D1] hover:bg-[#06D4C2] shadow-md'
                }`}
                title={micStatus !== 'idle' ? UI_TEXT.AI_SURVEY.CONVERSATIONAL.MIC_STOP_TITLE : UI_TEXT.AI_SURVEY.CONVERSATIONAL.MIC_START_TITLE}
              >
                {micStatus === 'listening' ? <Square className="h-7 w-7 text-white" /> : <Mic className="h-8 w-8 text-white" />}
              </button>
              <p className="text-xs text-[#9CA3AF] h-4">
                {micStatus === 'warming' ? UI_TEXT.AI_SURVEY.SESSION.MESSAGES.MIC_WARMING
                  : micStatus === 'listening' ? UI_TEXT.AI_SURVEY.CONVERSATIONAL.LISTENING_TAP_STOP
                  : isTranscribing ? UI_TEXT.AI_SURVEY.CONVERSATIONAL.TRANSCRIBING
                  : UI_TEXT.AI_SURVEY.CONVERSATIONAL.TAP_MIC_PROMPT}
              </p>
            </div>

            {/* Editable transcript */}
            <textarea
              value={response}
              onChange={(e) => { setResponse(e.target.value); setLastInputMode(INPUT_MODE.TEXT); }}
              disabled={isSubmitting || isTranscribing}
              placeholder={UI_TEXT.AI_SURVEY.CONVERSATIONAL.ANSWER_PLACEHOLDER}
              rows={4}
              className="mt-4 w-full border border-[#E5E7EB] rounded-lg p-3 text-[#374151] text-sm focus:outline-none focus:ring-2 focus:ring-[#07E5D1] focus:border-transparent resize-none"
            />

            <button
              onClick={handleSubmit}
              disabled={!response.trim() || busy || isTranscribing}
              className="mt-3 w-full flex items-center justify-center gap-2 bg-[#1C1C1E] text-white rounded-lg py-3 text-sm font-semibold hover:bg-[#374151] transition-colors disabled:opacity-40"
            >
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {isSubmitting ? UI_TEXT.AI_SURVEY.CONVERSATIONAL.SENDING : UI_TEXT.AI_SURVEY.CONVERSATIONAL.SEND_ANSWER}
            </button>
          </div>
        )}

        {error && (
          <div className="mt-4 text-sm text-[#EF4444] bg-red-50 border border-red-200 rounded-lg p-3">
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationalSurveyFlow;
